import type { MessengerViewName } from '@widgets/messenger/types'
import { messengerMaxViewScrollHeight, messengerWidgetWidth } from '@widgets/messenger/const'
import { useHtmlElementFromTemplateRef } from '@/composition/useHtmlElementFromTemplateRef'
import type { Ref } from '@/composition/vue/compositionApi'
import type { AnyTemplateElement } from '@/components/types'
import { useResizeObserver } from '@/composition/resizeObserver'
import { computed, ref } from '@/composition/vue/compositionApi'

type WidgetSizeObserverParams = {
  widgetRef: Ref<AnyTemplateElement | null>
  minimizedViewRef: Ref<AnyTemplateElement | null>
  expandedViewScrollRef: Ref<AnyTemplateElement | null>
  expandedViewContentRef: Ref<AnyTemplateElement | null>
  currentViewName: Ref<MessengerViewName>
}

export const useWidgetSizeObserver = ({
  widgetRef,
  minimizedViewRef,
  expandedViewScrollRef,
  expandedViewContentRef,
  currentViewName,
}: WidgetSizeObserverParams) => {
  const widgetElementRef = useHtmlElementFromTemplateRef(widgetRef)
  const minimizedViewElementRef = useHtmlElementFromTemplateRef(minimizedViewRef)
  const expandedViewScrollElementRef = useHtmlElementFromTemplateRef(expandedViewScrollRef)
  const expandedViewContentElementRef = useHtmlElementFromTemplateRef(expandedViewContentRef)

  const widgetHeight = ref(0)
  useResizeObserver(widgetElementRef, (entries) => {
    const target = entries[0].target as HTMLElement
    widgetHeight.value = target.offsetHeight
  })

  const minimizedViewSize = ref({
    width: 0,
    height: 0,
  })
  useResizeObserver(minimizedViewElementRef, (entries) => {
    const target = entries[0].target as HTMLElement
    minimizedViewSize.value = {
      width: target.offsetWidth,
      height: target.offsetHeight,
    }
  })

  const expandedViewHeight = ref(0)
  useResizeObserver(expandedViewScrollElementRef, (entries) => {
    const target = entries[0].target as HTMLElement
    expandedViewHeight.value = target.offsetHeight
  })

  const expandedViewScrollHeight = ref(0)
  useResizeObserver(expandedViewContentElementRef, (entries) => {
    const target = entries[0].target as HTMLElement
    expandedViewScrollHeight.value = target.offsetHeight
  })

  const expandedViewCustomHeight = ref(0)
  const setExpandedViewCustomHeight = (height: number) => {
    expandedViewCustomHeight.value = height
  }

  const size = computed(() => {
    if (currentViewName.value === 'minimized') {
      return minimizedViewSize.value
    }
    const effectiveContentHeight =
      expandedViewCustomHeight.value > 0 ? expandedViewCustomHeight.value : expandedViewScrollHeight.value

    const limitedContentHeight = Math.min(effectiveContentHeight, messengerMaxViewScrollHeight)

    return {
      width: messengerWidgetWidth,
      height: widgetHeight.value - expandedViewHeight.value + limitedContentHeight,
    }
  })

  return {
    size,
    setExpandedViewCustomHeight,
  }
}
